import React from 'react';
import { IToolManifest } from '../../../core/tools/interfaces/ITool';
import { SelectionTool } from '../../../core/tools/standard/SelectionTool';
import { Node, Edge } from '../../../core/models/scene';
import { ToolComponentFactory } from '../../../core/tools/components/ToolComponents';
import { MousePointer, CheckSquare, Square, Users } from 'lucide-react';

// Genogram-specific selection tool with family selection
export class GenogramSelectionTool extends SelectionTool {
  manifest: IToolManifest;
  
  private nodes: Node[] = [];
  private edges: Edge[] = [];
  private selectedIds: Set<string> = new Set();
  private setSelected: ((ids: Set<string>) => void) | null = null;
  
  constructor() {
    super();
    
    // Build manifest from base
    this.manifest = {
      ...this.getBaseManifest(),
      id: 'genogram-selection',
      supportedDiagramTypes: ['genogram'],
    } as IToolManifest;
  }
  
  setData(
    nodes: Node[],
    edges: Edge[],
    selectedIds: Set<string>,
    setSelected: (ids: Set<string>) => void
  ): void {
    this.nodes = nodes;
    this.edges = edges;
    this.selectedIds = selectedIds;
    this.setSelected = setSelected;
  }
  
  getAllItemIds(): string[] {
    return [
      ...this.nodes.map(n => n.id),
      ...this.edges.map(e => e.id)
    ];
  }
  
  setSelection(ids: Set<string>): void {
    if (this.setSelected) {
      this.setSelected(ids);
    }
  }
  
  getCurrentSelection(): Set<string> {
    return this.selectedIds;
  }
  
  getTotalItemCount(): number {
    return this.nodes.length + this.edges.length;
  }
  
  getConnectedItems(itemId: string): string[] {
    const connected: string[] = [];
    const visited = new Set<string>();
    
    // Find family members (recursive for full family tree)
    const findFamily = (id: string) => {
      if (visited.has(id)) return;
      visited.add(id);
      connected.push(id);
      
      const familyEdges = this.edges.filter(e => 
        (e.from === id || e.to === id) && 
        (e.type === 'marriage' || e.type === 'child')
      );
      
      familyEdges.forEach(edge => {
        connected.push(edge.id);
        const otherId = edge.from === id ? edge.to : edge.from;
        findFamily(otherId);
      });
    };
    
    findFamily(itemId);
    return connected;
  }
  
  // Use standardized toolbar component with family selection
  getToolbarComponent(): React.ComponentType<any> {
    return ({ onExecute }: any) => {
      const selectedCount = this.getCurrentSelection().size;
      const totalCount = this.getTotalItemCount();
      
      const handleSelectionMode = (mode: string) => {
        if (mode === 'connected' && selectedCount === 1) {
          const nodeId = Array.from(this.selectedIds)[0];
          onExecute({ mode: 'connected', nodeId });
        } else {
          onExecute({ mode });
        }
      };
      
      return ToolComponentFactory.createSelect({
        icon: MousePointer,
        placeholder: `Select (${selectedCount}/${totalCount})`,
        onValueChange: handleSelectionMode,
        options: [
          {
            value: 'all',
            label: 'Select All (Ctrl+A)',
            icon: CheckSquare,
            description: undefined
          },
          {
            value: 'none',
            label: 'Deselect All (Ctrl+D)',
            icon: Square,
            description: undefined
          },
          {
            value: 'invert',
            label: 'Invert Selection',
            icon: MousePointer,
            description: undefined
          },
          {
            value: 'connected',
            label: 'Select Family',
            icon: Users,
            description: selectedCount !== 1 ? 'Select exactly one node first' : undefined
          }
        ]
      });
    };
  }
}